import { TranslateService } from '@ngx-translate/core';
import dayjs, { Dayjs } from 'dayjs';
import {
  CampaignConfig,
  CampaignType,
  OneShotCampaignConfig,
  PeriodicCampaignConfig
} from '../../mediego-common-module/declarations/campaign-config';
import { ErrorsUtils } from '../../mediego-common-module/utils/errors.utils';

export const isPeriodicCampaign = (obj: CampaignConfig): obj is PeriodicCampaignConfig => {
  return obj && obj._type === CampaignType.Periodic;
};
export const isOneShotCampaign = (obj: CampaignConfig): obj is OneShotCampaignConfig => {
  return obj && obj._type === CampaignType.OneShot;
};

export const getFutureEvents = (campaign: CampaignConfig, dateLimit: Dayjs, countLimit?: number): Dayjs[] => {
  const getPeriodicCampaignFutureEvents = (_campaign: PeriodicCampaignConfig): Dayjs[] => {
    // Inspired from Cronicle : https://github.com/jhuckaby/Cronicle/blob/master/htdocs/js/home-worker.js#L104
    const filterDay = (date: Dayjs) => {
      const matchesMonth = !_campaign.timing.months || _campaign.timing.months.length === 0 || _campaign.timing.months.includes(date.month());
      const matchesDayOfMonth = !_campaign.timing.days || _campaign.timing.days.length === 0 || _campaign.timing.days.includes(date.date());
      const matchesDayOfWeek = !_campaign.timing.daysOfWeek || _campaign.timing.daysOfWeek.length === 0 || _campaign.timing.daysOfWeek.includes(date.day());

      return matchesMonth && matchesDayOfMonth && matchesDayOfWeek;
    };

    const futureEvents = [];

    main_loop:
    for (
      let cursor = dayjs().startOf('minute').add(1, 'minute'); // Start
      cursor.isBefore(dateLimit); // Finish
      cursor = cursor.add(1, 'day') // Step
    ) {
      if (filterDay(cursor)) {
        // Generate hours
        const hours = _campaign.timing.hours && _campaign.timing.hours.length ? _campaign.timing.hours : Array.from(Array(24).keys());

        for (const hour of hours) {
          // Generate minutes
          const minutes = _campaign.timing.minutes && _campaign.timing.minutes.length ? _campaign.timing.minutes : Array.from(Array(60).keys());

          for (const minute of minutes) {

            const nextEvent = cursor
              .set('hour', hour)
              .set('minute', minute);

            if (nextEvent.isAfter(dayjs())) {
              futureEvents.push(
                nextEvent
              );
            }

            if (countLimit && futureEvents.length === countLimit) {
              break main_loop;
            }
          }
        }
      }
    }

    return futureEvents;
  };

  const getOneShotCampaignFutureEvents = (): Dayjs[] => {
    const campaignDate = dayjs((campaign as OneShotCampaignConfig).timestamp);

    if (dayjs().isAfter(campaignDate) || campaignDate.isAfter(dateLimit)) {
      return [];
    }

    return [campaignDate];
  };

  if (isPeriodicCampaign(campaign)) {
    return getPeriodicCampaignFutureEvents(campaign);
  } else {
    return getOneShotCampaignFutureEvents();
  }

};

export const getNextExecutionDate = (campaign: CampaignConfig): Dayjs | undefined => {
  const getPeriodicCampaignNextExecutionDate = (): Dayjs => {
    return getFutureEvents(
      campaign,
      dayjs().add(4, 'year'),
      1
    )[0];
  };

  const getOneShotCampaignNextExecutionDate = (_campaign: OneShotCampaignConfig): Dayjs | undefined => {
    const campaignDate = dayjs(_campaign.timestamp);

    if (dayjs().isAfter(campaignDate)) {
      return;
    }

    return campaignDate;
  };

  if (isOneShotCampaign(campaign)) {
    return getOneShotCampaignNextExecutionDate(campaign);
  } else {
    return getPeriodicCampaignNextExecutionDate();
  }

};

export interface ApiError {
  errorId: string;
  message: string;
}

export const generateErrorDescription = (error: ApiError, defaultErrorMessage: string, translate: TranslateService): string => {

  switch (error?.errorId) {
    case ErrorsUtils.MISSING_ARTICLE:
      const indexAsString = /([^\d]|^)\d+([^\d]|$)/.exec(error?.message);
      const indexAsInt = indexAsString?.length
        ? parseInt(indexAsString[0], 10)
        : 0;
      // fallback to article 1
      return translate.instant('NEWSLETTERS.ERRORS.PREVIEW_FAILED.ARTICLE', {
        articleIndex: indexAsInt + 1
      });
    default:
      // No specific information available
      break;
  }

  return defaultErrorMessage;
};
